import * as Y from 'yjs';
import { WebrtcProvider } from 'y-webrtc';
import { supabase } from './supabaseClient';
interface User {
  id: string;
  name: string;
  color: string;
}

interface PresenceState {
  user: User;
  cursor: { x: number; y: number } | null;
}

export class CollaborationService {
  private doc: Y.Doc;
  private provider: WebrtcProvider;
  private awareness: any;

  constructor(strategyId: string) {
    this.doc = new Y.Doc();
    this.provider = new WebrtcProvider(`strategy-${strategyId}`, this.doc, {
      signaling: ['wss://signaling.yjs.dev'],
    });
    this.awareness = this.provider.awareness;
  }

  async initialize(initialBlocks: any[]): Promise<void> {
    try {
      const blocks = this.doc.getArray('blocks');

      // Only insert if blocks array is empty to avoid duplicates
      if (blocks.length === 0 && initialBlocks.length > 0) {
        // Insert each block individually
        initialBlocks.forEach((block, index) => {
          blocks.insert(index, [block]);
        });
      }

      // Set up presence
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (error) {
        console.warn('Failed to get user for collaboration:', error);
        return;
      }

      if (user) {
        this.awareness.setLocalState({
          user: {
            id: user.id,
            name: user.email || 'Anonymous',
            color: this.getRandomColor(),
          },
          cursor: null,
        });
      }
    } catch (error) {
      console.error('Failed to initialize collaboration:', error);
    }
  }

  subscribeToChanges(callback: (blocks: any[]) => void): () => void {
    const blocks = this.doc.getArray('blocks');

    const observer = () => {
      callback(blocks.toArray());
    };

    blocks.observe(observer);

    // Return unsubscribe function
    return () => {
      blocks.unobserve(observer);
    };
  }

  updateBlocks(blocks: any[]): void {
    try {
      const yBlocks = this.doc.getArray('blocks');

      // Clear existing blocks
      yBlocks.delete(0, yBlocks.length);

      // Insert new blocks individually
      blocks.forEach((block, index) => {
        yBlocks.insert(index, [block]);
      });
    } catch (error) {
      console.error('Failed to update blocks:', error);
    }
  }

  subscribeToPresence(callback: (users: User[]) => void): () => void {
    const presenceHandler = () => {
      const states = Array.from(
        this.awareness.getStates().values()
      ) as PresenceState[];
      const users = states
        .map((state) => state?.user)
        .filter((user): user is User => Boolean(user));
      callback(users);
    };

    this.awareness.on('change', presenceHandler);

    // Return unsubscribe function
    return () => {
      this.awareness.off('change', presenceHandler);
    };
  }

  updateCursor(position: { x: number; y: number } | null): void {
    try {
      const currentState =
        this.awareness.getLocalState() as PresenceState | null;
      if (currentState) {
        this.awareness.setLocalState({
          ...currentState,
          cursor: position,
        });
      }
    } catch (error) {
      console.error('Failed to update cursor:', error);
    }
  }

  destroy(): void {
    try {
      this.provider.destroy();
      this.doc.destroy();
    } catch (error) {
      console.error('Failed to destroy collaboration service:', error);
    }
  }

  private getRandomColor(): string {
    const colors = [
      '#2563eb', // blue-600
      '#dc2626', // red-600
      '#16a34a', // green-600
      '#9333ea', // purple-600
      '#ea580c', // orange-600
      '#0891b2', // cyan-600
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}

export const createCollaborationService = (
  strategyId: string
): CollaborationService => {
  return new CollaborationService(strategyId);
};
